import React, {lazy, Suspense, useEffect} from "react";
import {observer} from "mobx-react";
import isEmpty from "is-empty";
import useWithStore from "@/Hooks/useWithStore";
import useViewItemBatcher from "@/Hooks/useViewItemBatcher";
import {lazyWithPreload} from "@/Helpers/lazyWithPreload";
import LazySection from "@/Components/LazySection";
import BaseLayout from "@/Layouts/BaseLayout";
import Breadcrumbs from "@/Pages/Site/Catalog/Breadcrumbs";
import Filters from "@/Pages/Site/Catalog/Filters";
import Picture from "@/Components/Picture";
import CatalogCard from "@/Components/Site/CatalogCard";
import Tags from "@/Pages/Site/Catalog/Tags";
import {useAllStore} from "@/Stores/useAllStore";
import '@/Scss/Site/Catalog/index.scss';

const FilterDrawer = lazy(() => import('@/Components/Drawer/FilterDrawer'));
const FiltersDrawer = lazy(() => import('@/Pages/Site/Catalog/FiltersDrawer'));
const Pagination = lazyWithPreload(() => import('@/Components/Site/Pagination'));

const Index = (props) => {
    const {h1 = '', stores = {}, images = {}, products = [], isMobile = false, count = 0, seoText = ''} = props;
    const {catalogStore = {}} = stores;

    const {registerView} = useViewItemBatcher();

    useEffect(() => {
        catalogStore.start(props);
    }, [products]);

    useEffect(() => {
        catalogStore.startFilters(props);
    }, []);

    return (
        <BaseLayout {...props}>
            <section className={`section`}>
                <div className={`catalog`}>
                    {!isEmpty(images) && (
                        <div className={`catalog__image`}>
                            <Picture src={images.img_path} srcMobile={images.img_mobile_path} alt={h1} />
                        </div>
                    )}
                    <div className={`container`}>
                        <Breadcrumbs {...props} />
                        <div className={`catalogTitle`}>
                            <h1 className={`h1 h1--catalog`}>{h1}</h1>
                            {count !== 0 && (
                                <p className={`catalogTitle__count`}>
                                    | Найдено {count.toLocaleString('ru')}
                                </p>
                            )}
                        </div>
                        <Filters {...props} />
                        <div className={`catalogCards`}>
                            {count !== 0 ?
                                <>
                                    {products.map((product, i) => {
                                        return <CatalogCard
                                            key={product.id}
                                            el={product}
                                            {...props}
                                            type={`catalog`}
                                            index={(catalogStore.page - 1) * catalogStore.perPageStart + i + 1}
                                            registerView={registerView}
                                        />
                                    })}
                                </>
                                :
                                <>
                                    Не удалось найти украшения с указанными параметрами. Попробуйте сбросить
                                    фильтры.
                                </>
                            }
                        </div>
                        <ShowMore {...props} />
                        <LazySection>
                            <Pagination
                                activePage={catalogStore.activePage}
                                itemsCountPerPage={catalogStore.perPageStart}
                                totalItemsCount={count}
                                pageRangeDisplayed={isMobile ? 4 : 10}
                                onChange={catalogStore.handlePageChange}
                                hideFirstLastPages={isMobile}
                            />
                        </LazySection>
                        {(seoText !== '') && (<p className={`seo-text`}>{seoText}</p>)}
                        <Tags {...props} />
                    </div>
                </div>
            </section>
            <Modals {...props} />
        </BaseLayout>
    )
}

const ShowMore = observer((props) => {
    const {stores = {}, count = 0} = props;
    const {catalogStore = {}} = stores;

    if (catalogStore.activePage >= Math.ceil(count / catalogStore.perPageStart)) return null;

    return (
        <div className={`showMore`}>
            <div
                onClick={catalogStore.showMore}
                className={`button button--white${catalogStore.loaderShowBtn ? " load" : ""}`}
            >
                Показать еще
            </div>
        </div>
    );
});

const Modals = (props) => {
    const interactionFlag = useAllStore((state) => state.interactionFlag);

    if (!interactionFlag) return null;

    return (
        <>
            <Suspense><FilterDrawer {...props} /></Suspense>
            <Suspense><FiltersDrawer {...props} /></Suspense>
        </>
    );
};

export default useWithStore(Index);
